import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';

interface EmpresaDup {
  id: number;
  nome: string;
  cnpj: string | null;
  cidade: string | null;
  uf: string | null;
}

interface GrupoDuplicidade {
  principal: EmpresaDup;
  duplicatas: EmpresaDup[];
}

const IGNORED_KEY = 'hop_dup_ignorados';

function formatCNPJ(cnpj: string | null | undefined): string {
  if (!cnpj) return '—';
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

export default function Duplicidades({ password }: { password: string }) {
  const [grupos, setGrupos] = useState<GrupoDuplicidade[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ignorados, setIgnorados] = useState<number[]>([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('comercial-admin', {
        body: { action: 'duplicidades', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { grupos?: GrupoDuplicidade[]; total_empresas?: number; error?: string };
      if (parsed?.error) throw new Error(parsed.error);

      let ignorar: number[] = [];
      try {
        ignorar = JSON.parse(localStorage.getItem(IGNORED_KEY) ?? '[]');
      } catch {
        ignorar = [];
      }

      setIgnorados(ignorar);
      setGrupos((parsed?.grupos ?? []).filter((g) => !ignorar.includes(Number(g.principal.id))));
      setTotal(parsed?.total_empresas ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar duplicidades.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const ignorar = (principalId: number) => {
    const novos = Array.from(new Set([...ignorados, principalId]));
    setIgnorados(novos);
    localStorage.setItem(IGNORED_KEY, JSON.stringify(novos));
    setGrupos((prev) => prev.filter((g) => Number(g.principal.id) !== principalId));
  };

  const restaurados = () => {
    setIgnorados([]);
    localStorage.removeItem(IGNORED_KEY);
    load();
  };

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Possíveis duplicidades</h3>
          <p className="text-xs text-gray-500">
            {total != null ? `${total} empresas cadastradas · ` : ''}Sem fusão automática — apenas sinalização.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Atualizar
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mt-3">{error}</div>}

      {loading && grupos.length === 0 && !error ? (
        <div className="flex justify-center py-8"><div className="w-7 h-7 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" /></div>
      ) : grupos.length === 0 && !error ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 flex items-center justify-center bg-green-50 text-green-600 rounded-full mx-auto mb-3">
            <i className="ri-check-double-line text-xl"></i>
          </div>
          <p className="text-sm text-gray-500">Nenhuma duplicidade suspeita encontrada.</p>
          {ignorados.length > 0 && (
            <button onClick={restaurados} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] cursor-pointer">
              <i className="ri-arrow-go-back-line"></i> Mostrar {ignorados.length} alerta(s) ignorado(s)
            </button>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {grupos.map((g) => (
            <div key={g.principal.id} className="border border-amber-200 bg-amber-50/40 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg bg-white text-amber-600">
                  <i className="ri-ghost-smile-line"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{g.principal.nome}</p>
                  <p className="text-xs text-gray-500">
                    {formatCNPJ(g.principal.cnpj)} · {[g.principal.cidade, g.principal.uf].filter(Boolean).join(' - ') || '—'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to={`/painel/clientes/${g.principal.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] px-2 py-1 rounded-lg hover:bg-[#E8610A]/10 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-external-link-line"></i> Abrir
                  </Link>
                  <button
                    onClick={() => ignorar(Number(g.principal.id))}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-eye-off-line"></i> Ignorar
                  </button>
                </div>
              </div>

              <div className="mt-3 space-y-1.5 pl-11">
                {g.duplicatas.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 text-sm">
                    <i className="ri-corner-down-right-line text-gray-300"></i>
                    <span className="flex-1 text-gray-700 truncate">{d.nome}</span>
                    <span className="text-xs text-gray-400 hidden sm:inline">{formatCNPJ(d.cnpj)}</span>
                    <Link
                      to={`/painel/clientes/${d.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-[#E8610A] cursor-pointer whitespace-nowrap"
                    >
                      Abrir <i className="ri-external-link-line"></i>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}